import React, { useState, useEffect } from 'react'
import { Modal, Form } from 'react-bootstrap'
import { toast } from 'react-toastify'
import CustomButton from '../../Components/CustomButton'
import CustomInput from '../../Components/CustomInput'
import { useUpdateNeighborhoodMutation } from '../../Redux/Apis/Neighborhood'

const EditNeighborhoodModal = ({ show, onHide, neighborhood, onSuccess }) => {
  const [updateNeighborhood, { isLoading }] = useUpdateNeighborhoodMutation()

  const allSections = ['Clubhouse', 'Caddyshack', '19th Hole', 'The Pros', 'Rangefinder', 'The Barn', 'Calcutta']

  const [formData, setFormData] = useState({
    name: '',
    displayName: '',
    domain: '',
    apiUrl: '',
    branding: {
      primaryColor: '#193631',
      logo: '',
      favicon: '',
      splashScreen: ''
    },
    features: {
      sections: allSections,
      enableForums: true,
      enableDirections: true,
      enableChildTracking: true
    },
    contact: {
      supportEmail: '',
      phone: '',
      address: ''
    },
    mobileApps: {
      ios: {
        bundleId: '',
        appStoreId: ''
      },
      android: {
        packageName: '',
        playStoreId: ''
      }
    }
  })

  useEffect(() => {
    if (neighborhood && show) {
      setFormData({
        name: neighborhood.name || '',
        displayName: neighborhood.displayName || '',
        domain: neighborhood.domain || '',
        apiUrl: neighborhood.apiUrl || '',
        branding: {
          primaryColor: neighborhood.branding?.primaryColor || '#193631',
          logo: neighborhood.branding?.logo || '',
          favicon: neighborhood.branding?.favicon || '',
          splashScreen: neighborhood.branding?.splashScreen || ''
        },
        features: {
          sections: neighborhood.features?.sections || allSections,
          enableForums: neighborhood.features?.enableForums !== undefined ? neighborhood.features.enableForums : true,
          enableDirections: neighborhood.features?.enableDirections !== undefined ? neighborhood.features.enableDirections : true,
          enableChildTracking: neighborhood.features?.enableChildTracking !== undefined ? neighborhood.features.enableChildTracking : true
        },
        contact: {
          supportEmail: neighborhood.contact?.supportEmail || '',
          phone: neighborhood.contact?.phone || '',
          address: neighborhood.contact?.address || ''
        },
        mobileApps: {
          ios: {
            bundleId: neighborhood.mobileApps?.ios?.bundleId || '',
            appStoreId: neighborhood.mobileApps?.ios?.appStoreId || ''
          },
          android: {
            packageName: neighborhood.mobileApps?.android?.packageName || '',
            playStoreId: neighborhood.mobileApps?.android?.playStoreId || ''
          }
        }
      })
    }
  }, [neighborhood, show])

  const handleChange = (field, value) => {
    if (field.includes('.')) {
      const keys = field.split('.')
      setFormData(prev => {
        const updated = { ...prev }
        let current = updated
        for (let i = 0; i < keys.length - 1; i++) {
          current = current[keys[i]]
        }
        current[keys[keys.length - 1]] = value
        return updated
      })
    } else {
      setFormData(prev => ({ ...prev, [field]: value }))
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    try {
      await updateNeighborhood({
        id: neighborhood._id,
        payload: formData
      }).unwrap()

      toast.success('Neighborhood updated successfully')
      onSuccess()
      onHide()
    } catch (error) {
      toast.error(error?.data?.message || 'Failed to update neighborhood')
    }
  }

  return (
    <Modal show={show} onHide={onHide} size="xl" centered className="edit-neighborhood-modal">
      <Modal.Header closeButton className="modal-header-custom">
        <Modal.Title className="mainTitle">Edit Neighborhood</Modal.Title>
      </Modal.Header>

      <Modal.Body className="modal-body-custom">
        <div className="neighborhood-info-banner mb-4">
          <div className="d-flex align-items-center">
            <div className="neighborhood-icon me-3">
              <i className="fas fa-map-marker-alt fa-2x" style={{ color: formData.branding.primaryColor }}></i>
            </div>
            <div>
              <h5 className="mb-1 fw-bold">{neighborhood?.displayName}</h5>
              <p className="mb-0 text-muted">{neighborhood?.slug}</p>
            </div>
          </div>
        </div>

        <Form onSubmit={handleSubmit}>
          <div className="row">
            {/* Basic Information */}
            <div className="col-12 mb-3">
              <div className="section-divider">
                <h5 className="section-title">Basic Information</h5>
              </div>
            </div>

            <div className="col-md-6 mb-3">
              <CustomInput
                label="Name"
                type="text"
                placeholder="The Landings"
                value={formData.name}
                onChange={(e) => handleChange('name', e.target.value)}
                required
              />
            </div>

            <div className="col-md-6 mb-3">
              <CustomInput
                label="Display Name"
                type="text"
                placeholder="The Turn - Landings"
                value={formData.displayName}
                onChange={(e) => handleChange('displayName', e.target.value)}
                required
              />
            </div>

            <div className="col-md-6 mb-3">
              <CustomInput
                label="Domain"
                type="text"
                placeholder="landings.theturn.com"
                value={formData.domain}
                onChange={(e) => handleChange('domain', e.target.value)}
                required
              />
            </div>

            <div className="col-md-6 mb-3">
              <CustomInput
                label="API URL"
                type="text"
                placeholder="https://api.theturn.com"
                value={formData.apiUrl}
                onChange={(e) => handleChange('apiUrl', e.target.value)}
                required
              />
            </div>

            {/* Branding */}
            <div className="col-12 mb-3 mt-3">
              <div className="section-divider">
                <h5 className="section-title">Branding</h5>
              </div>
            </div>

            <div className="col-md-6 mb-3">
              <label className="mainLabel">Primary Color</label>
              <div className="d-flex align-items-center gap-2">
                <input
                  type="color"
                  className="form-control form-control-color"
                  value={formData.branding.primaryColor}
                  onChange={(e) => handleChange('branding.primaryColor', e.target.value)}
                  style={{ width: '60px', height: '40px', flexShrink: 0 }}
                />
                <input
                  type="text"
                  className="mainInput flex-grow-1"
                  value={formData.branding.primaryColor}
                  onChange={(e) => handleChange('branding.primaryColor', e.target.value)}
                />
              </div>
            </div>

            <div className="col-md-6 mb-3">
              <CustomInput
                label="Logo URL"
                type="text"
                placeholder="/assets/logo.png"
                value={formData.branding.logo}
                onChange={(e) => handleChange('branding.logo', e.target.value)}
              />
            </div>

            <div className="col-md-6 mb-3">
              <CustomInput
                label="Favicon URL"
                type="text"
                placeholder="/assets/favicon.png"
                value={formData.branding.favicon}
                onChange={(e) => handleChange('branding.favicon', e.target.value)}
              />
            </div>

            {/* Mobile Apps */}
            <div className="col-12 mb-3 mt-3">
              <div className="section-divider">
                <h5 className="section-title">Mobile Apps</h5>
              </div>
            </div>

            <div className="col-md-6 mb-3">
              <CustomInput
                label="iOS Bundle ID"
                type="text"
                placeholder="com.theturn.landings"
                value={formData.mobileApps.ios.bundleId}
                onChange={(e) => handleChange('mobileApps.ios.bundleId', e.target.value)}
              />
            </div>

            <div className="col-md-6 mb-3">
              <CustomInput
                label="iOS App Store ID"
                type="text"
                placeholder="1234567890"
                value={formData.mobileApps.ios.appStoreId}
                onChange={(e) => handleChange('mobileApps.ios.appStoreId', e.target.value)}
              />
            </div>

            <div className="col-md-6 mb-3">
              <CustomInput
                label="Android Package Name"
                type="text"
                placeholder="com.theturn.landings"
                value={formData.mobileApps.android.packageName}
                onChange={(e) => handleChange('mobileApps.android.packageName', e.target.value)}
              />
            </div>

            <div className="col-md-6 mb-3">
              <CustomInput
                label="Android Play Store ID"
                type="text"
                placeholder="com.theturn.landings"
                value={formData.mobileApps.android.playStoreId}
                onChange={(e) => handleChange('mobileApps.android.playStoreId', e.target.value)}
              />
            </div>

            {/* Contact */}
            <div className="col-12 mb-3 mt-3">
              <div className="section-divider">
                <h5 className="section-title">Contact Information</h5>
              </div>
            </div>

            <div className="col-md-6 mb-3">
              <CustomInput
                label="Support Email"
                type="email"
                placeholder="support@landings.theturn.com"
                value={formData.contact.supportEmail}
                onChange={(e) => handleChange('contact.supportEmail', e.target.value)}
              />
            </div>

            <div className="col-md-6 mb-3">
              <CustomInput
                label="Phone"
                type="text"
                placeholder="+1234567890"
                value={formData.contact.phone}
                onChange={(e) => handleChange('contact.phone', e.target.value)}
              />
            </div>

            <div className="col-md-6 mb-3">
              <CustomInput
                label="Address"
                type="text"
                placeholder="123 Main St"
                value={formData.contact.address}
                onChange={(e) => handleChange('contact.address', e.target.value)}
              />
            </div>

            {/* Features */}
            <div className="col-12 mb-3 mt-3">
              <div className="section-divider">
                <h5 className="section-title">Features</h5>
              </div>
            </div>

            <div className="col-md-12 mb-3">
              <div className="features-container">
                <div className="feature-item">
                  <Form.Check
                    type="checkbox"
                    id="feature-forums"
                    label="Enable Forums (19th Hole)"
                    checked={formData.features.enableForums}
                    onChange={(e) => handleChange('features.enableForums', e.target.checked)}
                  />
                  <small className="text-muted d-block ms-4">Allow users to create posts and discussions</small>
                </div>
                <div className="feature-item">
                  <Form.Check
                    type="checkbox"
                    id="feature-directions"
                    label="Enable Directions (Rangefinder)"
                    checked={formData.features.enableDirections}
                    onChange={(e) => handleChange('features.enableDirections', e.target.checked)}
                  />
                  <small className="text-muted d-block ms-4">Allow users to save and share directions</small>
                </div>
                <div className="feature-item">
                  <Form.Check
                    type="checkbox"
                    id="feature-tracking"
                    label="Enable Child Tracking"
                    checked={formData.features.enableChildTracking}
                    onChange={(e) => handleChange('features.enableChildTracking', e.target.checked)}
                  />
                  <small className="text-muted d-block ms-4">Allow parents to track children's locations</small>
                </div>
              </div>
            </div>
          </div>
        </Form>
      </Modal.Body>

      <Modal.Footer className="modal-footer-custom">
        <CustomButton
          variant="secondaryButton"
          text="Cancel"
          onClick={onHide}
          disabled={isLoading}
        />
        <CustomButton
          variant="primaryButton"
          text={isLoading ? 'Saving...' : 'Save Changes'}
          onClick={handleSubmit}
          disabled={isLoading}
        />
      </Modal.Footer>
    </Modal>
  )
}

export default EditNeighborhoodModal

